import { useEffect } from 'react';

import { sendHotjarEvent } from '~globals/common';
import { fireTrackEvent } from '~globals/tracking';

type Props = {
    params: {
        controls: number;
        enablejsapi?: number;
        rel?: number;
    };
    videoId: string;
};

const YouTube = ({ params, videoId }: Props) => {
    const milestones = [] as string[];
    let intervalId: NodeJS.Timeout;

    useEffect(() => {
        if (window.YT) {
            const container = document.getElementById(`video_${videoId}`);
            const oldIframe = container?.querySelector('iframe');
            if (oldIframe) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                container?.removeChild(oldIframe);

                const newDiv = document.createElement('div');
                newDiv.id = 'youtube_target';
                newDiv.className = 'w-full min-h-[281px] bg-cover bg-center relative';
                container?.prepend(newDiv);
            }
            onYTScriptLoad();
        }
    }, [videoId]);

    function onYTScriptLoad() {
        const ytVideoLoadInterval = setInterval(function startVideo() {
            if (window.YT?.Player) {
                clearInterval(ytVideoLoadInterval);
                window.player = new window.YT.Player(`youtube_target`, {
                    videoId: videoId,
                    height: 281,
                    width: 382,
                    playerVars: params,
                    events: {
                        onReady: onPlayerReady,
                        onStateChange: onPlayerStateChange,
                    },
                });
            }
        }, 100);
    }

    const onPlayerReady = (event: Event) => {
        (event.target as EventTarget & { playVideo: () => void }).playVideo();
    };

    const onPlayerStateChange = (event: Event & { data: string | number }) => {
        const action = event.data;
        const newMilestone = (percent: string) => {
            if (milestones.includes(percent)) return;

            milestones.push(percent);

            fireTrackEvent({
                eventList: [`video_${percent}`],
                page: { action: 'track YT video' },
                video: { title: window.player.videoTitle, id: videoId },
            });
        };

        const record = () => {
            if (window.player?.getDuration) {
                const duration = window.player.getDuration();
                const currentTime = window.player.getCurrentTime();
                const percent = Math.round((currentTime / duration) * 100);

                if (percent >= 25 && percent < 50) {
                    newMilestone('25');
                } else if (percent >= 50 && percent < 75) {
                    newMilestone('50');
                }
            }
        };

        if (action === window.YT.PlayerState.UNSTARTED) {
            sendHotjarEvent('Video_click');
            newMilestone('start');
        } else if (action === window.YT.PlayerState.ENDED) {
            clearInterval(intervalId);
            newMilestone('complete');
        } else if (action === window.YT.PlayerState.PLAYING) {
            intervalId = setInterval(record, 1000);
        } else if (action === window.YT.PlayerState.PAUSED) {
            clearInterval(intervalId);
        }
    };

    const buttonStyle: React.CSSProperties = {
        display: 'block',
        width: '68px',
        height: '48px',
        position: 'absolute',
        cursor: 'pointer',
        transform: 'translate3d(-50%, -50%, 0)',
        top: '50%',
        left: '50%',
        zIndex: '1',
        backgroundColor: 'transparent',
        backgroundImage:
            'url(\'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 68 48"><path d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.63 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z" fill="red"/><path d="M45 24 27 14v20" fill="white"/></svg>\')',
        filter: 'grayscale(100%)',
        transition: 'filter .1s cubic-bezier(0, 0, 0.2, 1)',
        border: 'none',
    };

    return (
        <button
            type="button"
            className="w-full min-h-[281px] appearance-none block"
            id={`video_${videoId}`}
            onClick={function handleLoadClick() {
                onYTScriptLoad();
            }}
            onKeyPress={function handleLoadClick() {
                onYTScriptLoad();
            }}
        >
            <div
                id={`youtube_target`}
                className="w-full min-h-[281px] bg-cover bg-center relative"
                style={{
                    backgroundImage: `url('https://i.ytimg.com/vi/${videoId}/hqdefault.jpg')`,
                }}
            >
                <div style={buttonStyle}></div>
            </div>
            <span className="sr-only">Click to start the YouTube video</span>
        </button>
    );
};

export default YouTube;
